/**
 * "공식 추가공간" — `progress` 7-stage 가 끝난 뒤 별도 stage 에서 촬영하도록
 * 미루는 `AdditionalSpaceObservingSession.spaceType` 집합.
 *
 * 사용자 정책 (2025-05):
 *
 *   - **공식 추가공간 (deferred to post-progress)**: 사전 정의된 enum 중 보조 공간
 *     성격 — 베란다(BALCONY), 세대창고(RESIDENCE_STORAGE) 등. 사용자가 사전에 그
 *     공간이 있는지 사용자/매니저가 확신하기 어려운 영역이라, 본 상담의 핵심 항목
 *     (이사일/주소/포장/이사유형/요청) 을 먼저 마친 뒤 마지막에 묶어서 촬영한다.
 *   - **pre-progress 유지**:
 *       - 주요공간 (ENTRANCE/LIVING_ROOM/KITCHEN/ROOM/BATH_ROOM) — 항상 존재한다고
 *         가정되는 공간. BATH_ROOM 은 ADDITIONAL_ROOM_TYPES 이지만 UX 상 주요공간
 *         취급 (`buildObservingPickerSections` 의 PRIMARY_TYPES 정책).
 *       - 사용자 자유 공간 (OTHER_SPACE) — 사용자가 "+ 공간 추가하기" 로 직접 추가한
 *         자유 공간. 사용자가 이미 등록을 의도한 공간이므로 pre-progress 에서 처리.
 *       - 운반 경로 (LADDER_TRUCK_ENTRANCE / FIRST_FLOOR_TRUCK_ENTRANCE / STAIRS) —
 *         backend 가 intake 단계에서 자동 등록. `canRecordMovingItems=false` 라
 *         resolver 의 `remainingTargets` 에는 들어가지 않지만 `hasPendingAdditional
 *         SpaceWork` 가 잡아 post-progress 단계에서 함께 노출된다 (기존 동작 유지).
 *
 * 본 집합에 spaceType 을 추가/제거하면:
 *   - `buildObservingPickerSections` 가 그 spaceType 의 linked/unlinked entry 를
 *     pre-progress picker 에서 자동으로 숨긴다.
 *   - `resolveCounselFlowStage.remainingTargets` 가 그 spaceType 의 session 을
 *     pre-progress 후보에서 제외 → 모든 다른 미완료가 끝나면 `progress` 로 전환.
 *   - `additional-spaces` stage 에서 `AdditionalSpaceFlowAdapter` 가 미완료
 *     공식 추가공간 + 잔여 추가공간(운반 경로 등) 을 함께 노출.
 */
export const FORMAL_ADDITIONAL_SPACE_TYPES: ReadonlySet<string> = new Set([
  'BALCONY',
  'RESIDENCE_STORAGE',
]);

/**
 * 주어진 `spaceType` 이 `FORMAL_ADDITIONAL_SPACE_TYPES` 에 속하는지.
 *
 * 빈 문자열 / null / undefined 는 false. spaceType 은 보통
 * `AdditionalSpaceObservingSession.spaceType` 또는 `SketchingSessionRoom.type`
 * 처럼 backend enum 직렬화 값 (대문자) 이다.
 */
export function isFormalAdditionalSpaceType(spaceType: string | null | undefined): boolean {
  if (!spaceType) return false;
  return FORMAL_ADDITIONAL_SPACE_TYPES.has(spaceType);
}
